import { useEffect, useState, type KeyboardEvent } from 'react';
import { List, ListItemButton, Paper, Typography } from '@mui/material';
import { fillTemplate, matchSlash, type SlashCommand } from './slash';

export const SLASH_MENU_ID = 'chat-slash-menu';

/** The slash menu of a message box: the commands that match what is typed, the highlighted one, and the keys that move and pick. */
export function useSlashMenu(input: string, commands: SlashCommand[], apply: (text: string, selection: [number, number]) => void) {
  const items = matchSlash(input, commands) ?? [];
  const [highlight, setHighlight] = useState(0);
  // Escape closes the menu until the input changes
  const [dismissedAt, setDismissedAt] = useState<string | null>(null);
  useEffect(() => setHighlight(0), [input]);
  const open = items.length > 0 && dismissedAt !== input;
  const current = Math.min(highlight, Math.max(items.length - 1, 0));

  const pick = (command: SlashCommand) => {
    const { text, selection } = fillTemplate(command.template);
    apply(text, selection);
  };

  /** Handles a key pressed in the message box while the menu is open; true when the menu used it. */
  const onKeyDown = (event: KeyboardEvent): boolean => {
    // A key that ends an input method's composition (Safari reports keyCode 229) belongs to the composition, not the menu
    if (!open || event.nativeEvent.isComposing || event.keyCode === 229) return false;
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlight((current + 1) % items.length);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        setHighlight((current - 1 + items.length) % items.length);
        return true;
      case 'Enter':
      case 'Tab':
        event.preventDefault();
        pick(items[current]!);
        return true;
      case 'Escape':
        event.preventDefault();
        setDismissedAt(input);
        return true;
      default:
        return false;
    }
  };

  return { open, items, current, pick, onKeyDown };
}

interface SlashMenuProps {
  items: SlashCommand[];
  current: number;
  onPick: (command: SlashCommand) => void;
}

/** The commands above the message box. */
export function SlashMenu({ items, current, onPick }: SlashMenuProps) {
  return (
    <Paper elevation={4} sx={{ position: 'absolute', left: 0, right: 0, bottom: '100%', mb: 1, zIndex: 10, maxHeight: 280, overflowY: 'auto' }}>
      <List id={SLASH_MENU_ID} role="listbox" aria-label="Commands" dense disablePadding sx={{ py: 0.5 }}>
        {items.map((command, index) => (
          <ListItemButton
            key={command.name}
            id={`${SLASH_MENU_ID}-${command.name}`}
            role="option"
            aria-selected={index === current}
            selected={index === current}
            // Picking with the mouse keeps the focus in the message box
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onPick(command)}
            sx={{ gap: 1.5 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 92 }}>
              /{command.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {command.description}
            </Typography>
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
}
